import { withAuthRecovery } from '@/lib/authRecovery'
import { getSupabase } from '@/lib/supabase'
import type { ManagementJobTitle, RoyaltyRole } from '@/types'

type RpcResult<T> = { data: T | null; error: unknown }
type RpcClient = {
  rpc: <T>(functionName: string, args?: Record<string, unknown>) => PromiseLike<RpcResult<T>>
}

function rpcClient(): RpcClient {
  return getSupabase() as unknown as RpcClient
}

export type ManagementUserRole =
  | 'management_company'
  | 'territorial_manager'
  | 'personal_manager'
  | 'trainer'
  | 'owner'
  | 'manager'
  | 'member'
  | 'user'

export type ManagementUserRoleGroup = 'management_company' | 'owners' | 'team' | 'other'

export type ManagementUser = {
  user_id: string
  telegram_id: number | null
  tg_username: string | null
  first_name: string | null
  last_name: string | null
  phone_number: string | null
  email: string | null
  email_verified_at: string | null
  auth_method: string | null
  has_telegram_provider: boolean
  has_email_provider: boolean
  role: ManagementUserRole
  role_group: ManagementUserRoleGroup
  access_level: string | null
  job_title: ManagementJobTitle
  position: string | null
  mc_is_active: boolean
  /**
   * Роль в контуре роялти (`management_company_access.royalty_role`) и app-роль
   * супер-админа. `list_management_users` их не отдаёт, поэтому подмешиваются из
   * `get_management_company_royalty_roles` / `get_management_company_super_admins` —
   * тех же функций, которыми их читает и пишет контур. `null` у роли роялти
   * означает «роли нет» ТОЛЬКО когда соответствующий флаг доступности `true`.
   */
  royalty_role: RoyaltyRole | null
  is_super_admin: boolean
  timezone: string | null
  notifications_enabled: boolean | null
  created_at: string | null
  last_seen_at: string | null
  access_disabled: boolean
  access_disabled_at: string | null
  access_disabled_reason: string | null
  access_disabled_by_user_id: string | null
  access_disabled_by_name: string | null
  /**
   * Города студий, к которым у пользователя есть доступ (владельцы и команды студий).
   * Считается в RPC через каноническую `get_user_studios_by_uuid`, поэтому совпадает с тем,
   * что человек реально видит в приложении. Для сотрудников УК и людей без роли — пустой
   * массив: УК видит всю сеть, такой тег не информативен.
   */
  studio_city_names: string[]
  total_count: number
}

type UserAccessStatus = Pick<
  ManagementUser,
  | 'user_id'
  | 'access_disabled'
  | 'access_disabled_at'
  | 'access_disabled_reason'
  | 'access_disabled_by_user_id'
  | 'access_disabled_by_name'
>

export type AuthRequestLogEntry = {
  id: number
  created_at: string
  function_name: string
  method: string
  outcome: 'success' | 'error'
  status_code: number
  error_code: string | null
  user_id: string | null
  user_display: string | null
  email: string | null
  telegram_id: number | null
  invite_token_present: boolean
  ip: string | null
  user_agent: string | null
  origin: string | null
  duration_ms: number | null
  details: Record<string, unknown> | null
}

/**
 * Справочник + две необязательные оси ролей. Флаги доступности отделяют «роли нет»
 * от «роли не загрузились»: гейт у обеих ролевых RPC свой (advanced ИЛИ роль), и
 * молча показать всем «без роли роялти» значило бы соврать про права.
 */
export type ManagementUserDirectory = {
  users: ManagementUser[]
  royaltyRolesAvailable: boolean
  superAdminFlagsAvailable: boolean
}

type RoyaltyRoleRow = { user_id: string; royalty_role: RoyaltyRole | null }
type SuperAdminRow = { user_id: string; is_super_admin: boolean }

export async function fetchManagementUsers(params?: {
  search?: string | null
  roleGroup?: ManagementUserRoleGroup | null
  limit?: number
  offset?: number
}): Promise<ManagementUserDirectory> {
  const [directoryResult, accessResult, royaltyResult, superAdminResult] = await Promise.all([
    withAuthRecovery(() =>
      rpcClient().rpc<ManagementUser[]>('list_management_users', {
        p_search: params?.search?.trim() || null,
        p_role_group: params?.roleGroup ?? null,
        p_limit: params?.limit ?? 500,
        p_offset: params?.offset ?? 0,
      })
    ),
    withAuthRecovery(() => rpcClient().rpc<UserAccessStatus[]>('list_user_access_statuses')),
    withAuthRecovery(() => rpcClient().rpc<RoyaltyRoleRow[]>('get_management_company_royalty_roles')),
    withAuthRecovery(() => rpcClient().rpc<SuperAdminRow[]>('get_management_company_super_admins')),
  ])

  if (directoryResult.error) throw directoryResult.error
  if (accessResult.error) throw accessResult.error

  const statusByUserId = new Map(
    ((accessResult.data ?? []) as UserAccessStatus[]).map(status => [status.user_id, status])
  )
  // Ролевые оси необязательны: экран открыт супер-админу, а `get_management_company_royalty_roles`
  // пропускает advanced ИЛИ роялти-super_admin — супер-админ со `standard` и без роли роялти
  // получит отказ. Это не повод ронять весь справочник, но и не повод выдавать отказ за «нет роли».
  const royaltyRolesAvailable = !royaltyResult.error
  const superAdminFlagsAvailable = !superAdminResult.error
  const royaltyRoleByUserId = new Map(
    ((royaltyResult.data ?? []) as RoyaltyRoleRow[]).map(row => [row.user_id, row.royalty_role])
  )
  const superAdminByUserId = new Map(
    ((superAdminResult.data ?? []) as SuperAdminRow[]).map(row => [row.user_id, row.is_super_admin === true])
  )

  // studio_city_names может отсутствовать, если миграция 20260730160000 ещё не применена
  // на окружении (фронт деплоится отдельно) — приводим к массиву, чтобы экран не падал.
  const users = ((directoryResult.data ?? []) as ManagementUser[]).map(row => {
    const status = statusByUserId.get(row.user_id)
    return {
      ...row,
      studio_city_names: row.studio_city_names ?? [],
      royalty_role: royaltyRoleByUserId.get(row.user_id) ?? null,
      // Признак ДЕЙСТВУЮЩИЙ, а не колонка: серверный `is_super_admin()` требует
      // `is_active`, поэтому у снятого сотрудника флаг в базе прав уже не даёт
      // (на PROD 02.09.2026 таких строк 0, но при возврате доступа флаг оживёт).
      is_super_admin: (superAdminByUserId.get(row.user_id) ?? false) && row.mc_is_active,
      access_disabled: status?.access_disabled ?? false,
      access_disabled_at: status?.access_disabled_at ?? null,
      access_disabled_reason: status?.access_disabled_reason ?? null,
      access_disabled_by_user_id: status?.access_disabled_by_user_id ?? null,
      access_disabled_by_name: status?.access_disabled_by_name ?? null,
    }
  })

  return { users, royaltyRolesAvailable, superAdminFlagsAvailable }
}

export async function setUserAccessDisabled(params: {
  userId: string
  disabled: boolean
  reason: string
}): Promise<void> {
  const { error } = await withAuthRecovery(() =>
    rpcClient().rpc<null>('set_user_access_disabled', {
      p_target_user_id: params.userId,
      p_disabled: params.disabled,
      p_reason: params.reason.trim(),
    })
  )
  if (error) throw error
}

export async function updateManagementMemberProfile(params: {
  userId: string
  firstName: string
  lastName: string
  position?: string | null
}): Promise<void> {
  const { error } = await withAuthRecovery(() =>
    rpcClient().rpc<null>('update_management_company_member_profile', {
      p_user_id: params.userId,
      p_first_name: params.firstName.trim(),
      p_last_name: params.lastName.trim(),
      p_position: params.position?.trim() || null,
    })
  )
  if (error) throw error
}

export async function fetchAuthRequestLog(params?: {
  limit?: number
  offset?: number
  email?: string | null
  method?: string | null
  onlyErrors?: boolean
  since?: string | null
}): Promise<AuthRequestLogEntry[]> {
  const { data, error } = (await withAuthRecovery(() =>
    rpcClient().rpc<AuthRequestLogEntry[]>('list_auth_request_log', {
      p_limit: params?.limit ?? 200,
      p_offset: params?.offset ?? 0,
      p_email: params?.email?.trim() || null,
      p_method: params?.method || null,
      p_only_errors: params?.onlyErrors ?? false,
      p_since: params?.since ?? null,
    })
  ))
  if (error) throw error
  return data ?? []
}
